import React from 'react';
import addDays from 'date-fns/addDays';
import compareAsc from 'date-fns/compareAsc';
import formatDate from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import startOfDay from 'date-fns/startOfDay';

import { Trans } from '@lyearn/i18n';
import {
  ScheduleInfoErrorConfig,
  validateScheduleInfo,
} from '@/components/Form/components/Frequency/utils';
import { FORMAT_24H } from '@/components/TimePicker';
import { EventFormInput } from '@/modules/events/views/EventEditor/types';
import {
  CronFrequencyEnum,
  EndScheduleInfoInput,
  EndScheduleTypeEnum,
  Maybe,
  ScheduleInfo,
  ScheduleInfoInput,
} from '@/types/schema';
import { getNextOccurrences } from '@/utils/backend/recurringNextOccurrences/recurringEventNextOccurrences';
import { getCurrentTimeZoneValue } from '@/utils/timezone';

const MAX_RECURRENCE_SESSIONS_COUNT = 60;

interface GetEndDateProps {
  startTime: string;
  recurrenceSchedule: Maybe<ScheduleInfo> | undefined;
}
export function getEndDateForRecurringEvent({
  startTime,
  recurrenceSchedule,
}: GetEndDateProps): Date {
  if (!startTime || !recurrenceSchedule) return new Date();

  const occurrences = getNextOccurrences(
    recurrenceSchedule,
    startTime,
    MAX_RECURRENCE_SESSIONS_COUNT,
  );

  const lastOccurrence = occurrences[occurrences.length - 1];

  if (!lastOccurrence) {
    return new Date();
  }

  return new Date(lastOccurrence);
}

export const getNewEndScheduleInfo = (
  endScheduleType?: EndScheduleTypeEnum,
): EndScheduleInfoInput => {
  if (endScheduleType === EndScheduleTypeEnum.Occurrences) {
    return {
      endScheduleType: EndScheduleTypeEnum.Occurrences,
      numberOfOccurrences: 5,
    };
  }

  return {
    endScheduleType: EndScheduleTypeEnum.Date,
    date: addDays(startOfDay(new Date()), 1).toISOString(),
  };
};

export const getNewRecurrenceSchedule = (values: EventFormInput): ScheduleInfoInput => ({
  frequency: CronFrequencyEnum.Daily,
  time: formatDate(new Date(values.startTime), FORMAT_24H),
  timezone: values.timezone || getCurrentTimeZoneValue(),
  endScheduleInfo: getNewEndScheduleInfo(),
});

const isStartAndEndDateValid = (startTime: string, endTime: string) =>
  compareAsc(parseISO(startTime), parseISO(endTime)) < 0;

export type RecurrenceScheduleErrorConfig = {
  scheduleInfo?: ScheduleInfoErrorConfig;
  endDate?: JSX.Element;
};

export const validateRecurrenceSchedule = ({
  scheduleInfo,
  startTimeISO,
}: {
  scheduleInfo: ScheduleInfoInput;
  startTimeISO?: string;
}) => {
  const errorConfig: RecurrenceScheduleErrorConfig = {};

  const validateScheduleInfoConfig = validateScheduleInfo(scheduleInfo);
  if (validateScheduleInfoConfig) errorConfig.scheduleInfo = validateScheduleInfoConfig;

  const endScheduleInfo = scheduleInfo.endScheduleInfo;
  if (endScheduleInfo?.endScheduleType === EndScheduleTypeEnum.Date) {
    if (!isStartAndEndDateValid(startTimeISO!, endScheduleInfo.date!)) {
      errorConfig.endDate = <Trans i18nKey="Events:Editor.RecurringScheduleError.EndDate" />;
    }
  }

  return Object.keys(errorConfig).length ? errorConfig : undefined;
};
